import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Navbar, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { 
  BookOpen, Users, Sparkles, Package, 
  Baby, Smile, User, Heart,
  Check
} from 'lucide-react';

const LandingPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleCTA = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      navigate('/register');
    }
  };

  const profiles = [
    { 
      key: 'child', 
      icon: Baby, 
      color: 'bg-yellow-100 text-yellow-600',
      image: 'https://images.unsplash.com/photo-1758612897112-ddf6bb6a455f?crop=entropy&cs=srgb&fm=jpg&q=85&w=400'
    },
    { 
      key: 'teen', 
      icon: Smile, 
      color: 'bg-orange-100 text-orange-600',
      image: 'https://images.unsplash.com/photo-1529390079861-591de354faf5?crop=entropy&cs=srgb&fm=jpg&q=85&w=400'
    },
    { 
      key: 'adult', 
      icon: User, 
      color: 'bg-blue-100 text-blue-600',
      image: 'https://images.unsplash.com/photo-1603560951924-cc96cf3b415c?crop=entropy&cs=srgb&fm=jpg&q=85&w=400'
    },
    { 
      key: 'senior', 
      icon: Heart, 
      color: 'bg-pink-100 text-pink-600',
      image: 'https://images.unsplash.com/photo-1641749631692-44ad83f07ced?crop=entropy&cs=srgb&fm=jpg&q=85&w=400'
    },
  ];

  const steps = [
    { key: 'step1', icon: BookOpen, color: 'bg-primary' },
    { key: 'step2', icon: Users, color: 'bg-secondary' },
    { key: 'step3', icon: Sparkles, color: 'bg-accent' },
    { key: 'step4', icon: Package, color: 'bg-primary' },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 hero-gradient">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-left"
            >
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground tracking-tight leading-tight">
                {t('hero.title')}
              </h1>
              <p className="mt-6 text-lg text-muted-foreground leading-relaxed max-w-xl">
                {t('hero.subtitle')}
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={handleCTA}
                  className="btn-primary text-lg"
                  data-testid="hero-cta"
                >
                  {t('hero.cta')}
                </Button>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="relative book-shadow rounded-2xl overflow-hidden aspect-[4/3]">
                <img 
                  src="/hero-family.jpg"
                  alt="Familia caucasiana compartiendo historias junto a la chimenea en una biblioteca cálida"
                  className="w-full h-full object-cover"
                  loading="eager"
                  decoding="async"
                  data-testid="landing-hero-image"
                />
                <div className="absolute inset-0 gradient-fade-bottom" />
              </div>
              <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-accent rounded-full flex items-center justify-center animate-float">
                <Sparkles className="w-10 h-10 text-foreground" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Profiles Section — HIDDEN (re-enable when all age formats are available) */}
      {false && (
      <section className="py-16 md:py-24 bg-white" id="profiles">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground">
              {t('profiles.title')}
            </h2>
            <p className="mt-4 text-muted-foreground text-base md:text-lg">
              {t('profiles.subtitle')}
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {profiles.map((profile, index) => (
              <motion.div
                key={profile.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="profile-card group cursor-pointer h-full" data-testid={`profile-${profile.key}`}>
                  <div className="aspect-[4/3] overflow-hidden">
                    <img 
                      src={profile.image}
                      alt={t(`profiles.${profile.key}`)}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  </div>
                  <CardContent className="p-6">
                    <div className={`w-12 h-12 rounded-full ${profile.color} flex items-center justify-center mb-4`}>
                      <profile.icon className="w-6 h-6" />
                    </div>
                    <h3 className="font-heading text-xl font-semibold text-foreground">
                      {t(`profiles.${profile.key}`)}
                    </h3>
                    <p className="mt-2 text-muted-foreground text-sm">
                      {t(`profiles.${profile.key}Desc`)}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* How It Works Section */}
      <section className="py-16 md:py-24 bg-muted/30" id="how-it-works">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground">
              {t('howItWorks.title')}
            </h2>
            <p className="mt-4 text-muted-foreground text-base md:text-lg">
              {t('howItWorks.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={step.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative"
                data-testid={`step-${index + 1}`}
              >
                <div className="flex flex-col items-center text-center">
                  <div className={`w-16 h-16 rounded-full ${step.color} flex items-center justify-center mb-6 text-white`}>
                    <step.icon className="w-8 h-8" />
                  </div>
                  <span className="absolute top-0 right-1/2 translate-x-1/2 -translate-y-2 text-6xl font-heading font-bold text-gray-100">
                    {index + 1}
                  </span>
                  <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
                    {t(`howItWorks.${step.key}.title`)}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {t(`howItWorks.${step.key}.desc`)}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 md:py-24 bg-white" id="pricing">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground">
              {t('pricing.title')}
            </h2>
            <p className="mt-4 text-muted-foreground text-base md:text-lg">
              {t('pricing.subtitle')}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Card className="card-elevated overflow-hidden" data-testid="pricing-card">
              <div className="bg-primary text-white p-8 text-center">
                <div className="flex items-baseline justify-center gap-2">
                  <span className="text-5xl font-heading font-bold">{t('pricing.basePrice')}</span>
                  <span className="text-white/80 text-lg">/ {t('pricing.basePriceLabel')}</span>
                </div>
                <div className="mt-3 flex items-center justify-center gap-4 text-white/70">
                  <span className="text-xl font-semibold text-white/90">{t('pricing.extraPrice')}</span>
                  <span>{t('pricing.extraPriceLabel')}</span>
                </div>
                <p className="mt-2 text-sm text-white/60">{t('pricing.shippingCollaborators')}</p>
              </div>
              <CardContent className="p-8">
                <ul className="space-y-3">
                  {['pages', 'format', 'contributors', 'ai', 'cover', 'reviews', 'extras'].map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-3.5 h-3.5 text-primary" />
                      </div>
                      <span className="text-sm text-foreground">{t(`pricing.features.${feature}`)}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 p-3 rounded-lg bg-amber-50 border border-amber-200">
                  <p className="text-xs text-amber-800">{t('pricing.collaboratorsNote')}</p>
                </div>
                <p className="mt-4 text-center text-sm text-muted-foreground font-medium">{t('pricing.deliveryTime')}</p>
                <Button 
                  onClick={handleCTA}
                  className="w-full mt-4 btn-primary text-lg"
                  data-testid="pricing-cta"
                >
                  {t('pricing.cta')}
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;
